import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client';
import { Dialog } from 'antd-mobile';
import { message } from 'antd';
import i18n from '../i18n';
import {isPda} from "../utils/appRuntime";
import { getRefreshToken, getUserInfo, saveUserInfo, removeUserInfo } from 'utils/auth';
import { setAllowNavigation } from '../hooks/usePreventEdgeSwipe';
import React from 'react';
import { isIOS, isAndroid } from 'utils/deviceUtils';
import { isAppVersionGreaterThanOrEqual, kNativeLoginVersion } from 'utils/versionUtils';
import { requestNativeLogout } from 'utils/nativeBridge';

// 定义全局类型
declare global {
  interface Window {
    __fieldErrors?: Record<string, string>;
  }
}

type TFunction = (key: string, options?: any) => string;
const t = i18n.t as TFunction;

// 全局标志位，防止重复显示设备被踢出弹窗
let deviceKickedOutDialogShown = false;

// 全局标志位，防止重复显示密码修改后踢出弹窗
let passwordChangedKickedOutDialogShown = false;

// Token 刷新状态
let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const resolvePendingRequests = () => {
  pendingRequests.forEach(({ resolve }) => resolve());
  pendingRequests = [];
};

const rejectPendingRequests = (reason?: unknown) => {
  pendingRequests.forEach(({ reject }) => reject(reason));
  pendingRequests = [];
};

/**
 * 通过 fetch 直接调用 refreshToken mutation，避免循环依赖（errorLink ↔ client）
 */
const fetchRefreshToken = async (refreshToken: string): Promise<{
  success: boolean;
  token?: string;
  refreshToken?: string;
}> => {
  const response = await fetch('/api/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `mutation RefreshToken($refreshToken: String!) {
        refreshToken(refreshToken: $refreshToken) {
          success
          message
          token
          refreshToken
        }
      }`,
      variables: { refreshToken },
    }),
  });
  const json = await response.json();
  return json.data?.refreshToken ?? { success: false };
};

const handleMobileLogout = (currentPath: string, messageKey?: string) => {
  if (messageKey) {
    const translated = t(messageKey);
    const translatedMessage = (translated && translated !== messageKey) ? translated : undefined;
    if (translatedMessage) {
      message.error(translatedMessage);
    }
  }
  removeUserInfo();
  setAllowNavigation(true);
  if (currentPath.includes('/pda/')) {
    window.location.href = '/pda/login';
  } else {
    window.location.href = '/login';
  }
};

// 错误处理链接
export const errorLink = onError(({graphQLErrors, networkError, operation, forward}) => {
    if (graphQLErrors) {
        for (const err of graphQLErrors) {
            const code = err.extensions?.code;
            const messageKey = err.extensions?.messageKey;

            // 默认使用错误消息
            let translatedMessage: string = t(messageKey as string) as string;

            const currentPath = window.location.pathname;
            // 根据错误码分类处理
            switch(code) {
                case 'UNAUTHORIZED': {
                    // 设备被踢出：弹窗提示后退出（native >= 8.8.0 确认后走 native logout）
                    if (messageKey === 'errors.auth.deviceKickedOut') {
                        if (!deviceKickedOutDialogShown) {
                            showDeviceKickedOutDialog(currentPath);
                        }
                        break;
                    } else if (messageKey === 'errors.auth.passwordChangedKickedOut') {
                        // 密码修改踢出：弹窗提示后退出（native >= 8.8.0 确认后走 native logout）
                        if (!passwordChangedKickedOutDialogShown) {
                            // 显示密码修改后踢出弹窗
                            showPasswordChangedKickedOutDialog(currentPath);
                        }
                        break;
                    } else {
                        // 普通未授权：>= 8.8.0 由 native 处理退出
                        if ((isIOS() || isAndroid()) && handleNativeUnauthorized()) {
                            break;
                        }
                    }

                    // 如果已在登录页，不处理
                    if (currentPath === '/login' || currentPath === '/pda/login') break;

                    const refreshToken = getRefreshToken();

                    // 无 refreshToken，直接登出
                    if (!refreshToken) {
                        console.error('UNAUTHORIZED, no refreshToken, path:', currentPath);
                        handleMobileLogout(currentPath, messageKey as string);
                        break;
                    }

                    // 如果正在刷新，排队等待
                    if (isRefreshing) {
                        return new Observable(observer => {
                            pendingRequests.push({
                                resolve: () => {
                                    const userInfo = getUserInfo();
                                    const newToken = userInfo?.token;
                                    if (newToken) {
                                        const oldHeaders = operation.getContext().headers;
                                        operation.setContext({
                                            headers: {
                                                ...oldHeaders,
                                                authorization: `Bearer ${newToken}`,
                                            },
                                        });
                                    }
                                    forward(operation).subscribe(observer);
                                },
                                reject: (error) => {
                                    observer.error(error);
                                },
                            });
                        });
                    }

                    // 开始刷新
                    isRefreshing = true;

                    return new Observable(observer => {
                        fetchRefreshToken(refreshToken).then((result) => {
                            if (result.success && result.token && result.refreshToken) {
                                // 刷新成功，保存新 Token
                                const userInfo = getUserInfo();
                                if (userInfo) {
                                    saveUserInfo({
                                        ...userInfo,
                                        token: result.token,
                                        refreshToken: result.refreshToken,
                                    });
                                }

                                // 更新当前请求的 header 并重试
                                const oldHeaders = operation.getContext().headers;
                                operation.setContext({
                                    headers: {
                                        ...oldHeaders,
                                        authorization: `Bearer ${result.token}`,
                                    },
                                });

                                // 处理排队的请求
                                resolvePendingRequests();

                                // 重试当前请求
                                forward(operation).subscribe(observer);
                            } else {
                                // 刷新失败
                                rejectPendingRequests();
                                handleMobileLogout(currentPath, messageKey as string);
                                observer.error(err);
                            }
                        }).catch(() => {
                            rejectPendingRequests();
                            handleMobileLogout(currentPath, messageKey as string);
                            observer.error(err);
                        }).finally(() => {
                            isRefreshing = false;
                        });
                    });
                }

                case 'PERMISSION':
                    message.error(translatedMessage);
                    console.error('PERMISSION, path:', currentPath, 'messageKey:', messageKey);
                    break;

                case 'FEATURE_NOT_ENABLED':
                    message.error(translatedMessage);
                    console.error('FEATURE_NOT_ENABLED, path:', currentPath, 'messageKey:', messageKey);
                    break;

                case 'VALIDATION_ERROR':
                case 'BUSINESS_ERROR':
                case 'INTERNAL_ERROR':
                default:
                    // 系统错误使用error
                    console.error('SYSTEM_ERROR, path:', currentPath, 'messageKey:', messageKey);
                    break;
            }
        }
    }

    if (networkError) {
        // message.error(t('errors.system.networkError'));
    }
});

/**
 * 显示设备被踢出弹窗
 */
function showDeviceKickedOutDialog(currentPath: string) {
    // 双重检查，确保不会重复显示
    if (deviceKickedOutDialogShown) {
        return;
    }

    // 立即设置标志位，防止在创建 Dialog 前被再次调用
    deviceKickedOutDialogShown = true;

    const t = i18n.t as TFunction;

    const messageText = t('errors.auth.deviceKickedOut.message');
    const warningText = t('errors.auth.deviceKickedOut.warning');

    Dialog.alert({
        title: t('errors.auth.deviceKickedOut.title'),
        content: React.createElement('div', null,
            React.createElement('div', {
                style: { marginBottom: '12px', fontSize: '14px', lineHeight: '20px' }
            }, messageText),
            React.createElement('div', {
                style: {
                    display: 'flex',
                    alignItems: 'center',
                    color: '#ff4d4f',
                    fontSize: '14px',
                    lineHeight: '20px'
                }
            },
                React.createElement('span', {
                    style: {
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#ff4d4f',
                        color: 'white',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginRight: '8px',
                        flexShrink: 0
                    }
                }, '!'),
                React.createElement('span', null, warningText)
            )
        ),
        confirmText: t('errors.auth.deviceKickedOut.confirm'),
        className: 'session-expired-dialog',
        onConfirm: () => {
            // 清理用户信息
            removeUserInfo();
            // >= 8.8.0：由 native 处理退出登录
            if ((isIOS() || isAndroid()) && handleNativeUnauthorized()) {
                return;
            }
            // 设置允许导航标记
            setAllowNavigation(true);
            // 重定向到登录页面
            if (currentPath.includes('/pda/')) {
                window.location.href = '/pda/login';
            } else {
                window.location.href = '/login';
            }
        },
        onClose: () => {
            // 只有在弹窗被关闭（非确认）时才重置标志位
            // 如果是通过 onConfirm 跳转，标志位保持为 true
            if (deviceKickedOutDialogShown) {
                deviceKickedOutDialogShown = false;
            }
        },
    });
}

/**
 * 显示密码修改后踢出弹窗
 */
function showPasswordChangedKickedOutDialog(currentPath: string) {
    // 双重检查，确保不会重复显示
    if (passwordChangedKickedOutDialogShown) {
        return;
    }

    // 立即设置标志位，防止在创建 Dialog 前被再次调用
    passwordChangedKickedOutDialogShown = true;

    const t = i18n.t as TFunction;

    Dialog.alert({
        title: t('errors.auth.passwordChangedKickedOut.title'),
        content: t('errors.auth.passwordChangedKickedOut.message'),
        confirmText: t('errors.auth.passwordChangedKickedOut.confirm'),
        className: 'session-expired-dialog',
        onConfirm: () => {
            // 清理用户信息
            removeUserInfo();
            // >= 8.8.0：由 native 处理退出登录
            if ((isIOS() || isAndroid()) && handleNativeUnauthorized()) {
                return;
            }
            // 设置允许导航标记
            setAllowNavigation(true);
            // 重定向到登录页面
            if (currentPath.includes('/pda/')) {
                window.location.href = '/pda/login';
            } else {
                window.location.href = '/login';
            }
        },
        onClose: () => {
            // 只有在弹窗被关闭（非确认）时才重置标志位
            // 如果是通过 onConfirm 跳转，标志位保持为 true
            if (passwordChangedKickedOutDialogShown) {
                passwordChangedKickedOutDialogShown = false;
            }
        },
    });
}

// 缓存版本检测结果，避免重复异步判断
let nativeLoginSupported: boolean | null = null;

/**
 * >= 8.8.0 时，UNAUTHORIZED 由 native 处理退出
 * 使用同步缓存判断，首次异步检测后缓存结果
 * @returns true 表示已由 native 处理，调用方应跳过后续逻辑
 */
function handleNativeUnauthorized(): boolean {
    if (nativeLoginSupported === true) {
        requestNativeLogout();
        return true;
    }
    if (nativeLoginSupported === false) {
        return false;
    }
    // 首次调用，异步检测并缓存
    isAppVersionGreaterThanOrEqual(kNativeLoginVersion).then((supported) => {
        nativeLoginSupported = supported;
        if (supported) {
            requestNativeLogout();
        }
    });
    // 首次无法同步判断，放行给原有逻辑兜底
    return false;
}